import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

const KEY = 'byelabel.finops.learn'

interface Ctx {
  learnMode: boolean
  toggle: () => void
}

const LearnContext = createContext<Ctx>({ learnMode: false, toggle: () => {} })

export function LearnModeProvider({ children }: { children: ReactNode }) {
  const [learnMode, setLearnMode] = useState<boolean>(() => {
    try { return localStorage.getItem(KEY) === 'on' } catch { return false }
  })

  useEffect(() => {
    try { localStorage.setItem(KEY, learnMode ? 'on' : 'off') } catch {}
    // Toggle a body class so global CSS can react if needed
    document.body.classList.toggle('learn-mode', learnMode)
  }, [learnMode])

  return (
    <LearnContext.Provider value={{ learnMode, toggle: () => setLearnMode(v => !v) }}>
      {children}
    </LearnContext.Provider>
  )
}

export const useLearnMode = () => useContext(LearnContext)

/* ---------- LearnTip component ----------
   Inline yellow explainer that appears beneath a metric when Learn mode is on.
   Use anywhere we want a "plain English" context line for a non-finance user.
*/
export function LearnTip({ children, className = '' }: { children: ReactNode; className?: string }) {
  const { learnMode } = useLearnMode()
  if (!learnMode) return null
  return (
    <div className={`mt-2 px-3 py-2 text-[11px] italic leading-snug text-amber-800 bg-amber-50 border-l-2 border-amber-400 rounded-r ${className}`}>
      <span className="not-italic mr-1">📖</span>{children}
    </div>
  )
}

/* ---------- InfoIcon component ----------
   Small (i) icon next to a metric label. Hovering shows a one-line tooltip
   via native browser title attribute. Works everywhere, accessible.
*/
export function InfoIcon({ tip }: { tip: string }) {
  return (
    <span
      title={tip}
      className="inline-flex items-center justify-center align-middle ml-1.5 w-3.5 h-3.5 rounded-full bg-ink-100 border border-ink-200 text-[8px] font-bold text-ink-500 cursor-help hover:bg-royal-500 hover:text-white hover:border-royal-500 transition-colors"
      aria-label={tip}
    >
      i
    </span>
  )
}
